'use client'

import { FC, useState } from 'react'
import { useTranslations } from 'next-intl'
import { Icons } from '@/app/components/ui/Icons'
import '@/app/styles/components/ShareButtons.css'

interface Props {
	title: string
	slug: string
	lang: string
}

const ShareButtons: FC<Props> = ({ title, slug, lang }) => {
	const t = useTranslations('Blog')
	const [copied, setCopied] = useState(false)

	// Construct the full URL (assuming the site URL is known or relative)
	// Ideally, we should use an environment variable for the base URL.
	// For now, I'll use window.location.origin on the client side.
	const getShareUrl = () => {
		if (typeof window !== 'undefined') {
			return `${window.location.origin}/${lang}/blog/${slug}`
		}
		return ''
	}

	const handleCopyLink = async () => {
		const url = getShareUrl()
		if (url) {
			try {
				await navigator.clipboard.writeText(url)
				setCopied(true)
				setTimeout(() => setCopied(false), 2000)
			} catch (err) {
				console.error('Failed to copy: ', err)
			}
		}
	}

	const handleShare = (platform: 'facebook' | 'twitter' | 'linkedin') => {
		const url = getShareUrl()
		if (!url) return

		let shareUrl = ''
		if (platform === 'facebook') {
			shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
		} else if (platform === 'twitter') {
			shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`
		} else if (platform === 'linkedin') {
			shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
		}

		window.open(shareUrl, '_blank', 'width=600,height=400')
	}

	return (
		<div className="share-buttons">
			<span className="share-buttons__label">{t('sharePost')}</span>
			<div className="share-buttons__list">
				<button
					onClick={handleCopyLink}
					className={`share-buttons__btn ${copied ? 'share-buttons__btn--copied' : ''}`}
					aria-label={t('copyLink')}
					title={t('copyLink')}
				>
					{copied ? (
						<>
							<svg
								width="20"
								height="20"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
								className="share-buttons__btn-icon"
							>
								<path
									d="M20 6L9 17L4 12"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
							<span className="share-buttons__btn-text">{t('copied')}</span>
						</>
					) : (
						<>
							<Icons.linkChain
								width="20"
								height="20"
								className="share-buttons__btn-icon"
							/>
							<span className="share-buttons__btn-text">{t('copyLink')}</span>
						</>
					)}
				</button>

				<button
					onClick={() => handleShare('facebook')}
					className="share-buttons__btn share-buttons__btn--social"
					aria-label="Share on Facebook"
				>
					<Icons.socialFacebook
						width="24"
						height="24"
						className="share-buttons__btn-icon"
					/>
				</button>

				<button
					onClick={() => handleShare('twitter')}
					className="share-buttons__btn share-buttons__btn--social"
					aria-label="Share on X"
				>
					<Icons.socialX
						width="24"
						height="24"
						className="share-buttons__btn-icon"
					/>
				</button>

				<button
					onClick={() => handleShare('linkedin')}
					className="share-buttons__btn share-buttons__btn--social"
					aria-label="Share on LinkedIn"
				>
					<Icons.socialLinkedin
						width="24"
						height="24"
						className="share-buttons__btn-icon"
					/>
				</button>
			</div>
		</div>
	)
}

export default ShareButtons
